import { IPageContent } from "api/interfaces/static-content.type";
import tryCatchWrapper from "modules/lib/tryCatchWrapper";
import { PAGE_TYPES } from "util/product/product-constants";
import { getRatingFromReviews } from "../../util/rating-review-helper";

interface IProps{
  staticContent: IPageContent;
}

const __getMetaData = ({
  staticContent, description, partNumber, manufacturer, initialPageTitle = "", initialPageDesc = "", longDescription = "", pageType,
}:IProps) => {
  const metaData = {};
  const variousManufacturers = "Various Manufacturers";
  const fallback = {};
  const makeModelsArr = getMakeModels(staticContent, pageType);
  const makeModelKeyword = pageType === PAGE_TYPES.PRODUCT
  && `, ${makeModelsArr.filter((_, idx) => idx <= 17).map((_) => `${_} ${description}`).join(", ")}`;
  if (manufacturer !== variousManufacturers) {
    const productTitle = longDescription || `${description} by ${manufacturer} - ${partNumber}`;
    fallback.pageTitle = `${initialPageTitle}${productTitle}`;

    fallback.metaDescription = initialPageDesc ? `${initialPageDesc} by ${manufacturer} - ${partNumber}` : `Buy ${manufacturer} ${partNumber} ${description}`;

    fallback.metaKeywords = `${manufacturer}, ${description}, ${partNumber}${makeModelKeyword}`;
  } else {
    const productTitle = longDescription || `${description} - ${partNumber}`;
    fallback.pageTitle = `${initialPageTitle}${productTitle}`;

    fallback.metaDescription = initialPageDesc ? `${initialPageDesc} - ${partNumber}` : `Buy ${manufacturer} ${partNumber} ${description}`;

    fallback.metaKeywords = `${description}, ${partNumber}${makeModelKeyword}`;
  }
  metaData.pageTitle = staticContent?.metaData?.title || fallback.pageTitle;

  metaData.metaDescription = pageType === PAGE_TYPES.PRODUCT ? fallback.metaDescription : staticContent?.metaData?.description || fallback.metaDescription;

  metaData.metaKeywords = pageType === PAGE_TYPES.PRODUCT ? fallback.metaKeywords : staticContent?.metaData?.keywordsV2 || fallback.metaKeywords;

  const compatibleText = " compatible with ";
  let runningLength = metaData.metaDescription.length + compatibleText.length;
  const makeModelString = makeModelsArr
    .filter((makeModelText) => {
      runningLength += makeModelText.length + ", ".length;
      return runningLength <= 165;
    })
    .join(", ");

  if (makeModelString.length) {
    metaData.metaDescription += `\n compatible with ${makeModelString}`;
  }
  return metaData;
};

function getMakeModels(staticContent: any, pageType: string) {
  const { makeModels } = staticContent?.links || {};
  if (makeModels?.length && pageType === PAGE_TYPES.PRODUCT) {
    return makeModels
      .filter((_: any) => _.pageDetails)
      .map((_: any) => `${_.pageDetails.displayableMake} ${_.pageDetails.displayableModel}`);
  }
  return [];
}

export const getMetaData = tryCatchWrapper(__getMetaData);

export const getReviewMeta = (reviews: any) => {
  if (!reviews.length) {
    return undefined;
  }
  return reviews.map((review: any) => ({
    "@type": "Review",

    reviewRating: {
      "@type": "Rating",
      ratingValue: `${review.rating || review.overall}`,
      bestRating: "5",
      worstRating: "1",
    },

    datePublished: review.createdDate || review.date,
    reviewBody: review.review,

    author: {
      "@type": "Person",
      name: review.reviewerName || review.name,
    },
  }));
};

export const aggregateRatingMeta = (reviews: any, aggregateRating?: number, totalReviews?: number) => {
  if (!((aggregateRating && totalReviews) || (reviews && reviews.length))) {
    return undefined;
  }

  const constAggRating = aggregateRating || getRatingFromReviews(reviews);
  const totatCount = totalReviews || reviews.length;

  return {
    "@type": "AggregateRating",
    bestRating: "5",
    ratingValue: `${constAggRating}`,
    reviewCount: `${totatCount}`,
  };
};

interface IAddress {
  streetAddress: string;
  addressLocality: string;
  addressRegion: string;
  postalCode: string;
  addressCountry:string;
}

export const organizationMicroData = (props : {type?: string, address: IAddress}) => {
  const { type, address, ...restProps } = props;
  return (
    {
      "@context": "https://schema.org",
      "@type": type || "LocalBusiness",
      name: "Parts Avatar",
      image: "https://partsavatar.ca/images/common/header-logo_16051144.png",
      "@id": "",
      url: "https://partsavatar.ca/",
      telephone: "",
      address: {
        "@type": "PostalAddress",
        ...address,
      },
      sameAs: [
        "https://www.facebook.com/partsavatarcanada/",
        "https://twitter.com/PartsAvatar",
        "https://www.instagram.com/partsavatarcanada/",
        "https://www.youtube.com/channel/UCQNwNyuU7GR08xn-Ac13uoA",
        "https://ca.linkedin.com/company/partsavatar-ca",
      ],
      ...restProps,
    }
  );
};
